"use client";

import * as React from "react";
import { motion } from "motion/react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { IconGripVertical, IconPlus } from "@tabler/icons-react";
import { Button } from "@heroui/react";
import type { BlockHighlightRect } from "./blockHighlightRect";
import { DragHandleMenu } from "./DragHandleMenu";
import { useBlockHandleOverlayState } from "./useBlockHandleOverlayState";
import { $createParagraphNode, $createTextNode, $getNodeByKey, $getRoot, $isParagraphNode } from "lexical";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isListItemNode } from "@lexical/list";
import { $focusImageBlockNode, $isImageBlockNode } from "../../kernel/nodes/ImageBlockNode";
import { resolveListItemDropIntent } from "./listDropHit";
import {
  buildListDragExpansionFromRoots,
  filterListDragRootKeys,
  type ListDragExpansionBlock,
} from "./listDragSubtree";
import {
  computeHandlePositionForKey,
  pickBlockKeyAtContainerPoint,
  pickBlockKeyAtViewportPoint,
} from "./blockHandleLayout";
import { SELECTED_BLOCK_BACKGROND_COLOR } from "../../kernel/constants";
import { readEmptyParagraphKeyForBlockHandle } from "./readEmptyParagraphKeyForBlockHandle";
import { slashMenuCancelledUntilNextSlashRef } from "../menus/slashMenuCancelledRef";

/** 插入线主条高度（px） */
const DROP_LINE_MAIN_H = 3;
/** 缩进区提示：每条短竖线 */
const INDENT_GUIDE_TICK_W = 2;
const INDENT_GUIDE_TICK_H = 12;
const INDENT_GUIDE_TICK_STEP = 10;
/** 右侧最近一条短线与主插入线起点之间的断开间距 */
const INDENT_GUIDE_GAP_BEFORE_MAIN = 14;

type DropLineState = {
  top: number;
  left: number;
  width: number;
  /** 列表拖拽：预览落点层级（1=最浅），短线数量与此一致 */
  indentGuideDepth?: number;
};

function unionBlockRectsForKeys(
  keys: string[],
  all: BlockHighlightRect[],
  container: HTMLDivElement | null,
  getElementByKey: (k: string) => HTMLElement | null,
): { top: number; left: number; width: number; height: number } | null {
  let minT = Infinity;
  let minL = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const k of keys) {
    let r = all.find((x) => x.key === k);
    if (!r && container) {
      const el = getElementByKey(k);
      if (!el) continue;
      const cRect = container.getBoundingClientRect();
      const br = el.getBoundingClientRect();
      r = {
        key: k,
        top: br.top - cRect.top + container.scrollTop,
        left: br.left - cRect.left + container.scrollLeft,
        width: br.width,
        height: br.height,
      };
    }
    if (!r) continue;
    minT = Math.min(minT, r.top);
    minL = Math.min(minL, r.left);
    maxR = Math.max(maxR, r.left + r.width);
    maxB = Math.max(maxB, r.top + r.height);
  }
  if (minT === Infinity) return null;
  return { top: minT, left: minL, width: maxR - minL, height: maxB - minT };
}

/** 悬层仅保留文字/列表等排版样式，去掉块级背景与阴影 */
function stripBackgroundFromElementTree(root: HTMLElement) {
  const walk = (el: HTMLElement) => {
    el.style.background = "transparent";
    el.style.backgroundColor = "transparent";
    el.style.backgroundImage = "none";
    el.style.boxShadow = "none";
    for (let i = 0; i < el.children.length; i++) {
      walk(el.children[i] as HTMLElement);
    }
  };
  walk(root);
}

export function BlockHandleOverlayPlugin({
  isEditable,
  containerRef,
}: {
  isEditable: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [editor] = useLexicalComposerContext();
  const {
    activeKey,
    selectedKeys,
    allRects,
    dragMenuOpen,
    dragMenuPos,
    dragMenuKey,
    onHandleClick,
    onRequestCloseMenu,
    beginBlockHandlePressSession,
    endBlockHandlePressSession,
  } = useBlockHandleOverlayState({ isEditable, containerRef });

  const [hoverBlockKey, setHoverBlockKey] = React.useState<string | null>(null);
  /** null=尚未有过指针进出（键盘用户仍显示手柄）；false=指针已离开编辑器；true=指针在编辑器内 */
  const [pointerOverEditor, setPointerOverEditor] = React.useState<boolean | null>(null);
  /**
   * mouse：显示手柄 + 正常光标
   * keyboard：正文内任意按键后进入，隐藏手柄与光标，直至任意 pointermove
   */
  const [editorPointerMode, setEditorPointerMode] = React.useState<"mouse" | "keyboard">("mouse");
  const editorPointerModeRef = React.useRef<"mouse" | "keyboard">("mouse");
  React.useEffect(() => {
    editorPointerModeRef.current = editorPointerMode;
  }, [editorPointerMode]);
  /** 滚动后强制重算手柄 DOM 位置（layout 不体现在 allRects 里） */
  const [layoutGen, setLayoutGen] = React.useState(0);
  const lastHoverClientRef = React.useRef<{ cx: number; cy: number } | null>(null);
  const hoverBlockKeyRef = React.useRef<string | null>(null);
  const activeKeyRef = React.useRef<string | null>(null);
  const selectedKeysRef = React.useRef<string[]>([]);
  const dragSessionKeysRef = React.useRef<string[] | null>(null);
  const dragMovedRef = React.useRef(false);
  const blockNextHandleClickRef = React.useRef(false);
  const dragPointerCaptureRef = React.useRef<{ el: HTMLElement; pointerId: number } | null>(null);
  /** 手柄按钮：超过阈值后才 setPointerCapture，避免吃掉 click */
  const dragHandleElementRef = React.useRef<HTMLElement | null>(null);
  /** pointerleave 与 setState(打开菜单) 同帧竞态时，用于保留 hover，避免手柄闪跳 */
  const dragMenuOpenRef = React.useRef(false);
  React.useEffect(() => {
    dragMenuOpenRef.current = dragMenuOpen;
  }, [dragMenuOpen]);

  React.useEffect(() => {
    hoverBlockKeyRef.current = hoverBlockKey;
  }, [hoverBlockKey]);

  React.useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);
  React.useEffect(() => {
    selectedKeysRef.current = selectedKeys;
  }, [selectedKeys]);

  /**
   * 手柄锚点块。菜单经 Portal 挂到 body 时，指针离开编辑器会触发 pointerleave 并清空 hover；
   * 图片块是 DecoratorNode，选区/光标常在相邻段落，`activeKey` 未必是图片 → 手柄会「溜」到别的块。
   * 菜单打开期间以 dragMenuKey 为准，与菜单目标块一致。
   */
  const displayKey =
    dragMenuOpen && dragMenuKey != null ? dragMenuKey : (hoverBlockKey ?? activeKey);

  const allRectsRef = React.useRef(allRects);
  React.useEffect(() => {
    allRectsRef.current = allRects;
  }, [allRects]);

  const draggingRef = React.useRef(false);
  const dragStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const [dropLine, setDropLine] = React.useState<DropLineState | null>(null);
  const [dragOffset, setDragOffset] = React.useState<{ dx: number; dy: number } | null>(null);
  const lastPointerYRef = React.useRef<number | null>(null);
  const lastPointerXRef = React.useRef<number | null>(null);
  const keysToMoveRef = React.useRef<string[]>([]);
  /** 列表：含子树时 = 所有被带起的 li key；用于落点排除与高亮 */
  const dragSurfaceKeysRef = React.useRef<string[]>([]);
  /** 列表：仅根 li（Lexical 移动只动根，子节点随树走） */
  const dragRootKeysRef = React.useRef<string[]>([]);
  const [dragGhosts, setDragGhosts] = React.useState<Array<{ key: string; top: number; left: number; width: number; height: number; html: string }> | null>(
    null,
  );

  React.useEffect(() => {
    if (dragSessionKeysRef.current != null) return;
    keysToMoveRef.current = selectedKeys.length ? selectedKeys : activeKey ? [activeKey] : [];
  }, [selectedKeys, activeKey]);

  /** 容器内 hover：手柄跟随指针所在块（嵌套列表取最深命中） */
  React.useEffect(() => {
    const el = containerRef.current;
    if (!isEditable || !el) return;
    let raf = 0;
    const applyHoverFromClient = (cx: number, cy: number) => {
      const c = containerRef.current;
      if (!c || draggingRef.current) return;
      const r = c.getBoundingClientRect();
      const x = cx - r.left + c.scrollLeft;
      const y = cy - r.top + c.scrollTop;
      const sorted = [...allRectsRef.current].sort((a, b) => a.top - b.top);
      const kRect = pickBlockKeyAtContainerPoint(x, y, sorted, editor);
      const kDom = pickBlockKeyAtViewportPoint(cx, cy, editor, c);
      // 几何命中优先：viewport DOM 在 Decorator/图片上偶发解析到邻块，会盖住 kRect 导致手柄锚错行或「消失」
      const k = kRect ?? kDom;
      // 指针在块与手柄之间的空白时命中为 null，勿清空 hover，否则手柄会「溜」到别处
      setHoverBlockKey((prev) => {
        if (k == null) return prev;
        return prev === k ? prev : k;
      });
    };
    const onMove = (e: PointerEvent) => {
      if (draggingRef.current) return;
      if (editorPointerModeRef.current === "keyboard") return;
      lastHoverClientRef.current = { cx: e.clientX, cy: e.clientY };
      const t = e.target as HTMLElement | null;
      // 指针在手柄区域时坐标往往落不到块矩形上，避免清空 hover 导致手柄闪没
      if (t?.closest?.("[data-block-handle-region]")) return;
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        applyHoverFromClient(e.clientX, e.clientY);
      });
    };
    const onEnter = () => {
      setPointerOverEditor(true);
    };
    const onLeave = () => {
      setPointerOverEditor(false);
      if (!draggingRef.current && !dragMenuOpenRef.current) setHoverBlockKey(null);
    };
    const onScroll = () => {
      if (editorPointerModeRef.current === "keyboard") return;
      const p = lastHoverClientRef.current;
      if (!p || draggingRef.current) return;
      setLayoutGen((g) => g + 1);
      applyHoverFromClient(p.cx, p.cy);
    };
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
      el.removeEventListener("scroll", onScroll);
    };
  }, [isEditable, containerRef, editor]);

  /** 正文内任意键盘动作 → 键盘模式（隐藏手柄与鼠标样式） */
  React.useEffect(() => {
    if (!isEditable) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const root = containerRef.current;
      if (!root) return;
      const t = e.target;
      if (!(t instanceof Node) || !root.contains(t)) return;
      const el = t instanceof Element ? t : t.parentElement;
      if (!el?.closest?.(".rt-content-editable")) return;
      if (editorPointerModeRef.current === "keyboard") return;
      editorPointerModeRef.current = "keyboard";
      setEditorPointerMode("keyboard");
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isEditable, containerRef]);

  /** 任意 pointermove 恢复鼠标模式（光标 + 手柄） */
  React.useEffect(() => {
    if (!isEditable) return;
    const onPointerMoveWake = () => {
      if (editorPointerModeRef.current !== "keyboard") return;
      editorPointerModeRef.current = "mouse";
      setEditorPointerMode("mouse");
    };
    document.addEventListener("pointermove", onPointerMoveWake, true);
    return () => document.removeEventListener("pointermove", onPointerMoveWake, true);
  }, [isEditable]);

  /** 键盘模式下隐藏编辑器内光标（含子元素、覆盖层） */
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.classList.toggle("rt-editor-keyboard-pointer-mode", editorPointerMode === "keyboard");
    return () => el.classList.remove("rt-editor-keyboard-pointer-mode");
  }, [editorPointerMode, containerRef]);

  /** 必须用稳定函数引用注册/移除监听；每 render 换新函数会导致 removeEventListener 失效、监听器堆叠 */
  const latestPointerMoveImpl = React.useRef<(e: PointerEvent) => void>(() => {});
  const latestPointerUpImpl = React.useRef<(e: PointerEvent) => void>(() => {});

  const stablePointerMove = React.useMemo(
    () => (e: PointerEvent) => {
      latestPointerMoveImpl.current(e);
    },
    [],
  );
  const stablePointerUp = React.useMemo(
    () => (e: PointerEvent) => {
      latestPointerUpImpl.current(e);
    },
    [],
  );

  const prepareDragVisuals = React.useCallback(() => {
    const keys = keysToMoveRef.current;
    const all = allRectsRef.current;
    const container = containerRef.current;
    const nextGhosts: Array<{ key: string; top: number; left: number; width: number; height: number; html: string }> = [];

    let listOnlyBase = false;
    let listDragBlocks: ListDragExpansionBlock[] = [];
    editor.getEditorState().read(() => {
      const base = keysToMoveRef.current;
      listOnlyBase = base.length > 0 && base.every((k) => $isListItemNode($getNodeByKey(k)));
      if (listOnlyBase) {
        const roots = filterListDragRootKeys(base, $getNodeByKey);
        const sortedForOrder = [...all].sort((a, b) => a.top - b.top);
        const docOrder = new Map(sortedForOrder.map((r, i) => [r.key, i]));
        const { blocks, surfaceKeys, moveRootKeys } = buildListDragExpansionFromRoots(
          roots,
          $getNodeByKey,
          docOrder,
        );
        listDragBlocks = blocks;
        dragRootKeysRef.current = moveRootKeys;
        dragSurfaceKeysRef.current = surfaceKeys;
      } else {
        listDragBlocks = [];
        dragRootKeysRef.current = [...base];
        dragSurfaceKeysRef.current = [...base];
      }
    });
    const buildShellHtml = (el: HTMLElement) => {
      const liClone = el.cloneNode(true) as HTMLElement;
      stripBackgroundFromElementTree(liClone);
      liClone.setAttribute("contenteditable", "false");
      liClone.style.userSelect = "none";
      liClone.style.pointerEvents = "none";
      let root: HTMLElement = liClone;
      const parentList = el.parentElement;
      if (el.tagName === "LI" && parentList && (parentList.tagName === "OL" || parentList.tagName === "UL")) {
        const shell = parentList.cloneNode(false) as HTMLElement;
        stripBackgroundFromElementTree(shell);
        shell.appendChild(liClone);
        root = shell;
      }
      return root.outerHTML;
    };

    if (listOnlyBase) {
      for (const block of listDragBlocks) {
        const rootKey = block.moveRootKeys[0];
        const subKeys = block.surfaceKeys;
        const union = unionBlockRectsForKeys(subKeys, all, container, (k) => editor.getElementByKey(k));
        const el = editor.getElementByKey(rootKey);
        if (!el || !union) continue;
        nextGhosts.push({
          key: rootKey,
          top: union.top,
          left: union.left,
          width: union.width,
          height: union.height,
          html: buildShellHtml(el),
        });
      }
    } else {
      for (const k of keys) {
        const el = editor.getElementByKey(k);
        if (!el) continue;
        let r = all.find((x) => x.key === k);
        if (!r && container) {
          const cRect = container.getBoundingClientRect();
          const br = el.getBoundingClientRect();
          r = {
            key: k,
            top: br.top - cRect.top + container.scrollTop,
            left: br.left - cRect.left + container.scrollLeft,
            width: br.width,
            height: br.height,
          };
        }
        if (!r) continue;
        nextGhosts.push({
          key: k,
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          html: buildShellHtml(el),
        });
      }
    }
    setDragGhosts(nextGhosts.length ? nextGhosts : null);
  }, [editor]);

  latestPointerMoveImpl.current = (e: PointerEvent) => {
    if (!dragStartRef.current) return;

    const ddx = Math.abs(e.clientX - dragStartRef.current.x) + Math.abs(e.clientY - dragStartRef.current.y);

    // 按下后先不进入拖拽态：超过阈值才出现插入线/悬层并捕获指针（轻点可正常触发 click → 菜单）
    if (!draggingRef.current) {
      if (ddx <= 5) return;
      draggingRef.current = true;
      dragMovedRef.current = true;
      prepareDragVisuals();
      setDragOffset({
        dx: e.clientX - dragStartRef.current.x,
        dy: e.clientY - dragStartRef.current.y,
      });
      const capEl = dragHandleElementRef.current;
      if (capEl) {
        try {
          capEl.setPointerCapture(e.pointerId);
          dragPointerCaptureRef.current = { el: capEl, pointerId: e.pointerId };
        } catch {
          dragPointerCaptureRef.current = null;
        }
      }
    }

    if (draggingRef.current) e.preventDefault();

    if (!draggingRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    const cRect = container.getBoundingClientRect();
    const pointerY = e.clientY - cRect.top + container.scrollTop;
    const pointerX = e.clientX - cRect.left + container.scrollLeft;
    lastPointerYRef.current = pointerY;
    lastPointerXRef.current = pointerX;
    if (dragStartRef.current) {
      setDragOffset({
        dx: e.clientX - dragStartRef.current.x,
        dy: e.clientY - dragStartRef.current.y,
      });
    }

    const sorted = [...allRectsRef.current].sort((a, b) => a.top - b.top);
    if (sorted.length === 0) {
      setDropLine(null);
      return;
    }

    const keysToMove = keysToMoveRef.current;
    const surfaceKeys = dragSurfaceKeysRef.current.length > 0 ? dragSurfaceKeysRef.current : keysToMove;
    let nextDropLine: DropLineState | null = null;

    editor.getEditorState().read(() => {
      const getListItemDepthPreview = (node: any) => {
        let depth = 0;
        let p: any = node;
        while (p) {
          if ($isListItemNode(p)) depth += 1;
          p = p.getParent?.();
        }
        return Math.max(1, depth);
      };

      const moving = keysToMove.map((k) => $getNodeByKey(k)).filter(Boolean) as any[];
      const listDrag = moving.length > 0 && moving.every((n) => $isListItemNode(n));

      if (listDrag) {
        const movedSet = new Set(surfaceKeys);
        const isUnderAnyMovedKey = (itemKey: string) => {
          let n: any = $getNodeByKey(itemKey);
          while (n) {
            if (movedSet.has(n.getKey())) return true;
            n = n.getParent?.();
          }
          return false;
        };
        const listRects = sorted.filter((r) => {
          const n = $getNodeByKey(r.key);
          return $isListItemNode(n);
        });
        const targetRects = listRects.filter((r) => !movedSet.has(r.key) && !isUnderAnyMovedKey(r.key));
        const depthForKey = (itemKey: string) => {
          const n = $getNodeByKey(itemKey);
          return $isListItemNode(n) ? getListItemDepthPreview(n) : 0;
        };
        const intent = resolveListItemDropIntent(pointerY, targetRects, depthForKey);
        if (!intent) return;
        const row = listRects.find((x) => x.key === intent.targetKey);
        if (!row) return;
        const lineTop = intent.placement === "before" ? row.top : row.top + row.height;
        const targetNode = $getNodeByKey(intent.targetKey);
        let indentGuideDepth: number | undefined;
        if ($isListItemNode(targetNode)) {
          // 与落点一致：层级仅由命中的目标行（Y + 最深 li）决定，不叠加横向 depthDelta
          indentGuideDepth = Math.max(1, Math.min(8, getListItemDepthPreview(targetNode)));
        }
        nextDropLine = { top: lineTop, left: row.left, width: row.width, indentGuideDepth };
      } else {
        let idx = sorted.findIndex((r) => pointerY < r.top + r.height / 2);
        if (idx < 0) idx = sorted.length - 1;
        const next = sorted[idx] as BlockHighlightRect;
        if (idx === sorted.length - 1 && pointerY >= next.top + next.height / 2) {
          nextDropLine = { top: next.top + next.height, left: next.left, width: next.width };
        } else {
          nextDropLine = { top: next.top, left: next.left, width: next.width };
        }
      }
    });

    setDropLine(nextDropLine);
  };

  latestPointerUpImpl.current = (e: PointerEvent) => {
    /** 已超过阈值并进入过拖拽态（与轻点区分） */
    const didCommitDrag = draggingRef.current;
    let pointerY = lastPointerYRef.current;
    let pointerX = lastPointerXRef.current;
    const containerEl = containerRef.current;
    if (containerEl && (pointerY == null || pointerX == null)) {
      const cRect = containerEl.getBoundingClientRect();
      pointerY = e.clientY - cRect.top + containerEl.scrollTop;
      pointerX = e.clientX - cRect.left + containerEl.scrollLeft;
    }
    const keysToMove = keysToMoveRef.current;
    const listDragSurfaceSnapshot =
      dragSurfaceKeysRef.current.length > 0 ? [...dragSurfaceKeysRef.current] : null;
    const listDragRootSnapshot = dragRootKeysRef.current.length > 0 ? [...dragRootKeysRef.current] : null;

    draggingRef.current = false;
    dragStartRef.current = null;
    dragHandleElementRef.current = null;
    if (didCommitDrag) blockNextHandleClickRef.current = true;
    dragSessionKeysRef.current = null;
    dragMovedRef.current = false;
    setDropLine(null);
    setDragOffset(null);
    setDragGhosts(null);
    dragSurfaceKeysRef.current = [];
    dragRootKeysRef.current = [];

    lastPointerYRef.current = null;
    lastPointerXRef.current = null;

    // 支持 paragraph/heading/listitem 拖拽重排
    if (didCommitDrag && pointerY != null && keysToMove.length) {
      const sorted = [...allRectsRef.current].sort((a, b) => a.top - b.top);
      if (sorted.length) {
        editor.update(() => {
          const movingNodes = keysToMove
            .map((k) => $getNodeByKey(k))
            .filter(Boolean) as any[];
          if (movingNodes.length === 0) return;
          const movingIsListItem = movingNodes.every((n) => $isListItemNode(n));

          // 列表项专用移动路径：只以 listitem 为候选，避免落到 paragraph/heading 导致插入失败
          if (movingIsListItem) {
            const listRects = sorted.filter((r) => {
              const n = $getNodeByKey(r.key);
              return $isListItemNode(n);
            });
            if (listRects.length === 0) return;

            const surfaceKeys = listDragSurfaceSnapshot ?? keysToMove;
            const rootKeys = listDragRootSnapshot ?? keysToMove;
            const rootSet = new Set(rootKeys);

            const movedSet = new Set(surfaceKeys);
            const isUnderAnyMovedKey = (itemKey: string) => {
              let n: any = $getNodeByKey(itemKey);
              while (n) {
                if (movedSet.has(n.getKey())) return true;
                n = n.getParent?.();
              }
              return false;
            };
            // 不能把落点选在「正在移动的列表项」的子树上，否则 insertBefore/After 会破坏结构
            const targetRects = listRects.filter((r) => !movedSet.has(r.key) && !isUnderAnyMovedKey(r.key));
            if (targetRects.length === 0) return;

            const nodesToMoveOrdered = listRects
              .map((r) => $getNodeByKey(r.key))
              .filter((n): n is any => !!n && rootSet.has(n.getKey()) && $isListItemNode(n));
            if (nodesToMoveOrdered.length === 0) return;

            const depthForKey = (itemKey: string) => {
              const n = $getNodeByKey(itemKey);
              if (!$isListItemNode(n)) return 0;
              let depth = 0;
              let p: any = n;
              while (p) {
                if ($isListItemNode(p)) depth += 1;
                p = p.getParent?.();
              }
              return Math.max(0, depth);
            };
            const intent = resolveListItemDropIntent(pointerY, targetRects, depthForKey);
            if (!intent) return;

            const targetNode = $getNodeByKey(intent.targetKey);
            if (!$isListItemNode(targetNode)) return;

            // 多块根（父 li + Lexical 缩进壳 li）时，不能每次都 target.insertBefore(x)：
            // 会得到 … W, A, target，破坏「A → 壳 W」顺序，易触发 List 合并/子级挂错项。
            if (intent.placement === "before") {
              let ref: any = targetNode;
              for (let i = nodesToMoveOrdered.length - 1; i >= 0; i--) {
                const moved = nodesToMoveOrdered[i];
                ref.insertBefore(moved, false);
                ref = moved;
              }
            } else {
              let ref: any = targetNode;
              for (let i = 0; i < nodesToMoveOrdered.length; i++) {
                const moved = nodesToMoveOrdered[i];
                ref.insertAfter(moved, false);
                ref = moved;
              }
            }

            // 层级仅由目标行决定：与目标 li 成同级 sibling，不再根据指针 X 二次 indent/outdent
            (nodesToMoveOrdered[0] as any).selectStart?.();
            return;
          }

          let idx = sorted.findIndex((r) => pointerY < r.top + r.height / 2);
          if (idx < 0) idx = sorted.length - 1;
          const next = sorted[idx] as BlockHighlightRect;
          const atEnd = idx === sorted.length - 1 && pointerY >= next.top + next.height / 2;
          const insertionBeforeKey = atEnd ? null : next.key;

          const root = $getRoot();
          const orderedNodes: any[] = [];
          const walk = (node: any) => {
            if ($isListItemNode(node)) {
              orderedNodes.push(node);
              return; // 避免收集 listitem 内部 paragraph
            }
            if ($isImageBlockNode(node)) {
              orderedNodes.push(node);
              return;
            }
            if ($isParagraphNode(node) || $isHeadingNode(node)) {
              const parent = node?.getParent?.();
              if ($isListItemNode(parent)) return;
              orderedNodes.push(node);
            }
            const children = node?.getChildren?.() ?? [];
            for (const child of children) walk(child);
          };
          for (const child of root.getChildren()) walk(child);
          const originalKeys = orderedNodes.map((n) => n.getKey());
          const movedSet = new Set(keysToMove);

          if (originalKeys.length === 0) {
            root.append($createParagraphNode());
            return;
          }

          const nodesToMoveOrdered = originalKeys
            .filter((k) => movedSet.has(k))
            .map((k) => $getNodeByKey(k))
            .filter(
              (n): n is any =>
                $isParagraphNode(n) || $isHeadingNode(n) || $isListItemNode(n) || $isImageBlockNode(n),
            );

          if (nodesToMoveOrdered.length === 0) return;

          const remainingKeys = originalKeys.filter((k) => !movedSet.has(k));

          // 目标插入点：
          // - atEnd => 追加到最后（refNode = null）
          // - 否则 => 在“移除 moved 后”的剩余节点列表中，取对应索引处的第一个节点作为 refNode
          let refKey: string | null = null;
          if (!atEnd) {
            const desiredKey = insertionBeforeKey;
            const desiredIdx = desiredKey ? originalKeys.indexOf(desiredKey) : originalKeys.length;
            const beforeCount = remainingKeys.filter((k) => originalKeys.indexOf(k) < desiredIdx).length;
            refKey = remainingKeys[beforeCount] ?? null;
          }

          const refNode = refKey ? $getNodeByKey(refKey) : null;

          // 若 refNode 存在：从后往前 insertBefore，保证 nodesToMoveOrdered 的相对顺序不反转
          if (refNode) {
            for (let i = nodesToMoveOrdered.length - 1; i >= 0; i--) {
              (refNode as any).insertBefore(nodesToMoveOrdered[i], false);
            }
          } else {
            // refNode 不存在 => 追加到末尾；listitem 通过最后一个剩余节点 insertAfter 保持合法结构
            const first = nodesToMoveOrdered[0];
            const lastRemainingKey = remainingKeys[remainingKeys.length - 1] ?? null;
            const lastRemainingNode = lastRemainingKey ? $getNodeByKey(lastRemainingKey) : null;
            if ($isListItemNode(first) && lastRemainingNode) {
              for (let i = nodesToMoveOrdered.length - 1; i >= 0; i--) {
                (lastRemainingNode as any).insertAfter?.(nodesToMoveOrdered[i], false);
              }
            } else {
              for (const n of nodesToMoveOrdered) {
                root.append(n);
              }
            }
          }

          const firstMoved = nodesToMoveOrdered[0];
          if ($isImageBlockNode(firstMoved)) $focusImageBlockNode(firstMoved);
          else (firstMoved as any).selectStart?.();
        });

        editor.focus();
      }
    }

    const cap = dragPointerCaptureRef.current;
    dragPointerCaptureRef.current = null;
    if (cap) {
      try {
        if (cap.el.hasPointerCapture(cap.pointerId)) cap.el.releasePointerCapture(cap.pointerId);
      } catch {
        /* ignore */
      }
    }

    document.removeEventListener("pointermove", stablePointerMove, true);
    document.removeEventListener("pointerup", stablePointerUp, true);
    endBlockHandlePressSession();
  };

  const beginHandleDrag = React.useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      onRequestCloseMenu();
      const session =
        selectedKeysRef.current.length > 0
          ? [...selectedKeysRef.current]
          : hoverBlockKeyRef.current ?? activeKeyRef.current
            ? [hoverBlockKeyRef.current ?? activeKeyRef.current!]
            : [];
      if (session.length === 0) return;
      beginBlockHandlePressSession();
      // 轻点：不进入 dragging、不出现插入线；超过 ~5px 移动后才 prepareDragVisuals + capture（见 pointermove）
      dragSessionKeysRef.current = session;
      keysToMoveRef.current = session;
      dragMovedRef.current = false;
      draggingRef.current = false;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      dragHandleElementRef.current = e.currentTarget as HTMLElement;
      dragPointerCaptureRef.current = null;
      setDropLine(null);
      const containerEl = containerRef.current;
      if (containerEl) {
        const cRect = containerEl.getBoundingClientRect();
        lastPointerYRef.current = e.clientY - cRect.top + containerEl.scrollTop;
        lastPointerXRef.current = e.clientX - cRect.left + containerEl.scrollLeft;
      }
      document.addEventListener("pointermove", stablePointerMove, true);
      document.addEventListener("pointerup", stablePointerUp, true);
    },
    [onRequestCloseMenu, beginBlockHandlePressSession, stablePointerMove, stablePointerUp, containerRef],
  );

  const handleMotionPos = React.useMemo(() => {
    if (!isEditable || dragOffset != null) return null;
    // 拖拽菜单挂在 body Portal 上，指针移向菜单时会离开 containerRef，不能因此收起手柄锚点
    if (pointerOverEditor === false && !dragMenuOpen) return null;
    if (editorPointerMode === "keyboard") return null;
    const c = containerRef.current;
    if (!displayKey || !c) return null;
    return computeHandlePositionForKey(displayKey, editor, c);
  }, [
    isEditable,
    displayKey,
    editor,
    dragOffset,
    allRects,
    layoutGen,
    pointerOverEditor,
    dragMenuOpen,
    editorPointerMode,
  ]);

  const dragMenuViewportPos = React.useMemo(() => {
    if (!dragMenuOpen || !handleMotionPos || !containerRef.current) return dragMenuPos;
    const c = containerRef.current;
    const cr = c.getBoundingClientRect();
    return {
      top: cr.top + handleMotionPos.top - c.scrollTop,
      left: cr.left + handleMotionPos.left - c.scrollLeft,
    };
  }, [dragMenuOpen, dragMenuPos, handleMotionPos, layoutGen]);

  const plusParagraphKey = React.useMemo(
    () => readEmptyParagraphKeyForBlockHandle(editor, displayKey ?? null),
    [editor, displayKey, allRects, layoutGen],
  );

  const onSlashPlusPress = React.useCallback(() => {
    const pk = plusParagraphKey;
    if (!pk) return;
    onRequestCloseMenu();
    slashMenuCancelledUntilNextSlashRef.current = false;
    editor.update(() => {
      const node = $getNodeByKey(pk);
      if (!$isParagraphNode(node) || node.getTextContent().trim() !== "") return;
      node.clear();
      node.append($createTextNode("/"));
      node.select(1, 1);
    });
    editor.focus();
  }, [editor, onRequestCloseMenu, plusParagraphKey]);

  if (!isEditable) return null;

  const selectedSet = new Set(selectedKeys);
  const selectedRects = allRects.filter((rect) => selectedSet.has(rect.key));

  return (
    <>
      {selectedRects.length > 0 ? (
        <div className="pointer-events-none absolute inset-0 z-[1]">
          {selectedRects.map((rect) => (
            <div
              key={rect.key}
              className={`absolute rounded-sm ${SELECTED_BLOCK_BACKGROND_COLOR}`}
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
              }}
            />
          ))}
        </div>
      ) : null}

      {dropLine != null ? (
        <>
          {dropLine.indentGuideDepth != null && dropLine.indentGuideDepth > 0
            ? Array.from({ length: dropLine.indentGuideDepth }, (_, i) => {
                const tickTop = dropLine.top - (INDENT_GUIDE_TICK_H - DROP_LINE_MAIN_H) / 2;
                const d = dropLine.indentGuideDepth as number;
                // 从左到右：最左=最浅层，靠主线一侧=当前预览层（与列表缩进方向一致）
                const tickLeft =
                  dropLine.left -
                  INDENT_GUIDE_GAP_BEFORE_MAIN -
                  INDENT_GUIDE_TICK_W -
                  (d - 1 - i) * INDENT_GUIDE_TICK_STEP;
                return (
                  <div
                    key={i}
                    className="pointer-events-none absolute rounded-[1px]"
                    style={{
                      top: tickTop,
                      left: tickLeft,
                      width: INDENT_GUIDE_TICK_W,
                      height: INDENT_GUIDE_TICK_H,
                      background: "var(--color-default-500)",
                      opacity: 0.42,
                      zIndex: 5,
                    }}
                  />
                );
              })
            : null}
          <div
            className="pointer-events-none absolute bg-accent"
            style={{
              top: dropLine.top,
              left: dropLine.left,
              width: dropLine.width,
              height: DROP_LINE_MAIN_H,
              borderRadius: 999,
              zIndex: 5,
            }}
          />
        </>
      ) : null}

      {/* 全局 hover：手柄弹簧曲线跟到指针所在块；拖拽时隐藏 */}
      {handleMotionPos ? (
        <motion.div
          data-block-handle-region
          className="absolute z-[2]"
          style={{ position: "absolute" }}
          initial={false}
          animate={{
            top: handleMotionPos.top,
            /** Grip 左缘始终对齐 `computeHandlePositionForKey`；「+」不参与 motion.left，避免显隐时整轨横移 + 弹簧绕弯 */
            left: handleMotionPos.left,
          }}
          transition={{
            top: { type: "spring", stiffness: 420, damping: 28, mass: 0.72 },
            left: { type: "tween", duration: 0 },
          }}
        >
          <div className="relative flex items-center">
            {plusParagraphKey ? (
              <div className="absolute right-full top-0 mr-1 flex h-7 items-center">
                <Button
                  type="button"
                  variant="ghost"
                  data-add-block-plus
                  aria-label="斜杠命令菜单"
                  className="h-7 min-h-7 shrink-0 rounded-sm px-1 py-0 text-[var(--accent)]"
                  onPointerDown={(e) => e.stopPropagation()}
                  onPress={onSlashPlusPress}
                >
                  <IconPlus className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="Select block"
              data-block-drag-handle="true"
              className="h-7 w-5 min-h-7 min-w-5 shrink-0 rounded-sm p-0"
              onPointerDown={(e) => {
                beginHandleDrag(e);
              }}
              onPress={(e) => {
                if (blockNextHandleClickRef.current) {
                  blockNextHandleClickRef.current = false;
                  return;
                }
                if (!displayKey) return;
                onHandleClick(displayKey, { shiftKey: Boolean(e.shiftKey) });
              }}
            >
              <IconGripVertical className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      ) : null}

      {/* 拖拽悬层：携带块内容（样式一致） */}
      {dragOffset != null && dragGhosts?.length ? (
        <div className="pointer-events-none absolute inset-0" style={{ zIndex: 6 }}>
          {dragGhosts.map((g) => (
            <div
              key={g.key}
              className="rt-drag-ghost"
              style={{
                position: "absolute",
                top: g.top,
                left: g.left,
                width: g.width,
                minHeight: Math.max(20, g.height),
                overflow: "visible",
                transform: `translate(${dragOffset.dx}px, ${dragOffset.dy}px)`,
                opacity: 0.98,
              }}
              dangerouslySetInnerHTML={{ __html: g.html }}
            />
          ))}
        </div>
      ) : null}

      <DragHandleMenu
        isEditable={isEditable}
        dragMenuOpen={dragMenuOpen}
        dragMenuPos={dragMenuViewportPos ?? dragMenuPos}
        dragMenuKey={dragMenuKey}
        selectedKeys={selectedKeys}
        onRequestClose={onRequestCloseMenu}
        containerRef={containerRef}
      />
    </>
  );
}

